import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listPlans, closePaymentSuccessModal } from "../../store/billingSlice";
import { getPastTransactions } from "../../store/invoiceSlice";
import { usePaymentHandlers } from "../../hooks/usePaymentHandlers";
import { useBillingHandlers } from "../../hooks/useBillingHandlers";
import { loadRazorpaySDK } from "../../utils/razorpayUtils";
import { transformPlans } from "../../utils/planUtils";
import { usePageTitle } from "../../hooks/usePageTitle";
import { hasActivePlan, getPlanIds } from "../../utils/capabilitiesHelper";
import { BillingTabs } from "../../components/whatsapp/billing/components/BillingTabs";
import { SubscriptionPlansTab } from "../../components/whatsapp/billing/components/SubscriptionPlansTab";
import { BillingHistoryTab } from "../../components/whatsapp/billing/components/BillingHistoryTab";
import { PaymentSuccessModal } from "../../components/whatsapp/billing/components/PaymentSuccessModal";
import { MetaWalletTab } from "../../components/whatsapp/billing/wallet/MetaWalletTab";
import { useWalletQueryParams } from "../../components/whatsapp/billing/wallet/hooks/useWalletQueryParams";
import { WA_PAGE_BG, waShell } from "./utils/whatsappPageShell";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { BillingSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";

export default function Billing() {
  const dispatch = useDispatch();
  const billing = useSelector((state) => state.billing);
  const invoice = useSelector((state) => state.invoice);
  const capabilities = useSelector((state) => state.capabilities.capabilities);
  const companyId = useSelector((state) => state.auth?.user?.companyId);
  const phones = useSelector((state) => state.whatsapp?.phones) || [];
  const selectedPhone = useSelector((state) => state.whatsapp?.selectedPhone);

  const [activeTab, setActiveTab] = useState("subscription-plans");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [currentView, setCurrentView] = useState("plans");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("active");
  const [isContinuingToCheckout, setIsContinuingToCheckout] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  usePageTitle(
    activeTab === "billing-history"
      ? "Billing History"
      : activeTab === "meta-wallet"
        ? "Wallet"
        : "Billing & Subscription"
  );

  const hasPlanIdValue = hasActivePlan(capabilities);
  const PLANS = transformPlans(billing.plans);
  const selectedPlanDetails = PLANS.find((plan) => plan.id === selectedPlan);
  const planIds = getPlanIds(capabilities);
  const userPlanId = planIds.length > 0 ? planIds[0] : null;
  const currentPlanDetails = userPlanId ? PLANS.find((plan) => plan.id === userPlanId) : selectedPlanDetails;
  const _priceExponent = selectedPlanDetails?.price?.exponent ?? 2;
  const _priceDivisor = Math.pow(10, _priceExponent);
  const gstAmount = (selectedPlanDetails?.price?.gstAmount ?? 0) / _priceDivisor;
  const totalAmount = (selectedPlanDetails?.price?.amountWithGst ?? 0) / _priceDivisor;
  const showZeroMarkup = selectedPlanDetails?.period === "month" || selectedPlanDetails?.period === "quarter";

  const activeSubscription = capabilities?.context?.subscriptions?.find((sub) => sub?.status === 'ACTIVE')
    ?? capabilities?.context?.subscriptions?.[0];
  const subscriptionStartDate = activeSubscription?.startDate ? new Date(activeSubscription.startDate) : null;
  const subscriptionEndDate = activeSubscription?.endDate ? new Date(activeSubscription.endDate) : null;

  useEffect(() => {
    if (billing.plans.length === 0) {
      dispatch(listPlans());
    }
    if (phones.length === 0) {
      dispatch({ type: "FETCH_WABA_IDS" });
    }
  }, [dispatch, billing.plans.length, phones.length]);

  useEffect(() => {
    setActiveTab("subscription-plans");
  }, [hasPlanIdValue]);

  useEffect(() => {
    loadRazorpaySDK().catch(() => {});
  }, []);

  useWalletQueryParams({ setActiveTab, setShowRechargeModal });

  useEffect(() => {
    if (activeTab === "billing-history" && invoice.transactions.length === 0) {
      dispatch(getPastTransactions());
    }
  }, [activeTab, invoice.transactions.length, dispatch]);

  useEffect(() => {
    if (currentView === "plans") {
      setIsContinuingToCheckout(false);
    }
  }, [currentView]);

  const onPaymentSuccessModalClose = () => {
    dispatch(closePaymentSuccessModal());
  };

  const onGoToBillingHistory = () => {
    setActiveTab("billing-history");
    dispatch(getPastTransactions());
  };

  const { handlePaymentSuccess, handlePaymentDismissed, handlePaymentFailure } = usePaymentHandlers({
    billing,
    selectedPlanDetails,
    setSubscriptionStatus,
    setCurrentView,
    setIsProcessingPayment,
  });

  const { handleContinueToCheckout, handleInitiatePayment, handleSaveBillingDetails } = useBillingHandlers({
    billing,
    companyId,
    selectedPlan,
    selectedPlanDetails,
    isContinuingToCheckout,
    setIsContinuingToCheckout,
    setCurrentView,
    handlePaymentSuccess,
    handlePaymentDismissed,
    handlePaymentFailure,
  });

  const currentNumber = phones.length > 0 ? (phones.find(n => n.id === selectedPhone) || phones[0]) : null;
  const displayPhoneNumber = currentNumber?.number || currentNumber?.displayPhoneNumber || currentNumber?.phoneNumber;

  const metaWalletContent = (
    <MetaWalletTab
      phoneNumber={displayPhoneNumber || ""}
      showRechargeModal={showRechargeModal}
      setShowRechargeModal={setShowRechargeModal}
    />
  );

  const subscriptionPlansContent = (
    <SubscriptionPlansTab
      currentView={currentView}
      hasPlanId={hasPlanIdValue}
      PLANS={PLANS}
      billingTabs={billing.billingTabs}
      selectedPlan={selectedPlan}
      setSelectedPlan={setSelectedPlan}
      handleContinueToCheckout={handleContinueToCheckout}
      isContinuingToCheckout={isContinuingToCheckout}
      selectedPlanDetails={selectedPlanDetails}
      userPlanId={userPlanId}
      gstAmount={gstAmount}
      totalAmount={totalAmount}
      showZeroMarkup={showZeroMarkup}
      billing={billing}
      isProcessingPayment={isProcessingPayment}
      handleInitiatePayment={handleInitiatePayment}
      handleSaveBillingDetails={handleSaveBillingDetails}
      setCurrentView={setCurrentView}
      subscriptionStartDate={subscriptionStartDate}
      subscriptionEndDate={subscriptionEndDate}
    />
  );

  const billingHistoryContent = (
    <BillingHistoryTab
      invoice={invoice}
      subscriptionStatus={subscriptionStatus}
      currentPlanDetails={currentPlanDetails}
      hasPlanId={hasPlanIdValue}
      companyId={companyId}
      handleInitiatePayment={handleInitiatePayment}
      setActiveTab={setActiveTab}
    />
  );

  return (
    <div className={waShell("w-full min-h-full", WA_PAGE_BG, "px-4 sm:px-6 lg:px-8 py-5 sm:py-6", "animate-fade-in")}>
      <div className="mx-auto w-full max-w-5xl animate-fade-in-up space-y-5">
        {billing.loadingPlans ? (
          <BillingSkeleton embedded />
        ) : (
          <>
            <WhatsAppPageIntro
              description={
                activeTab === "billing-history"
                  ? "Invoices and payment records for your organization."
                  : activeTab === "meta-wallet"
                    ? "Manage your WhatsApp messaging credits and recharge your prepaid wallet."
                    : "Manage your subscription, invoices, and wallet."
              }
            />
            <BillingTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              subscriptionPlansTab={subscriptionPlansContent}
              billingHistoryTab={billingHistoryContent}
              metaWalletTab={metaWalletContent}
            />
          </>
        )}
      </div>

      <PaymentSuccessModal
        isOpen={billing.showPaymentSuccessModal}
        onClose={onPaymentSuccessModalClose}
        onGoToBillingHistory={onGoToBillingHistory}
        paymentData={billing.paymentSuccessData}
        companyId={companyId}
      />
    </div>
  );
}
